import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const Navbar = () => {
  const { user, profile, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const isAdmin = profile?.role === 'admin';
  const isEntreprise = profile?.role === 'entreprise';
  const isCandidat = profile?.role === 'candidat';

  return (
    <nav className="bg-white shadow-sm py-4">
      <div className="container mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <h1 className="text-2xl font-bold text-brandBlue">FeedMyBack</h1>
        </Link>
        <div className="hidden md:flex items-center space-x-8">
          <Link to="/" className="text-gray-700 hover:text-brandBlue transition">Accueil</Link>
          {user ? (
            <>
              {(isEntreprise || isAdmin) && (
                <Link to="/create-feedback" className="text-gray-700 hover:text-brandBlue transition">
                  Créer un feedback
                </Link>
              )}
              <Link to="/dashboard" className="text-gray-700 hover:text-brandBlue transition">
                Mon espace feedbacks
              </Link>
              <Link to="/settings" className="text-gray-700 hover:text-brandBlue transition">
                Paramètres
              </Link>
            </>
          ) : (
            <Link to="/auth?tab=register" className="text-gray-700 hover:text-brandBlue transition">
              Faire une demande de feedback
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" className="text-gray-700 hover:text-brandBlue transition font-medium">
              Administration
            </Link>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {profile?.full_name ? getInitials(profile.full_name) : user.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{profile?.full_name || 'Utilisateur'}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                    <p className="text-xs leading-none text-muted-foreground mt-1 capitalize">
                      {profile?.role === 'admin' ? 'Administrateur' : profile?.role === 'entreprise' ? 'Recruteur' : 'Candidat'}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard">
                    Mon espace feedbacks
                  </Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/admin">Administration</Link>
                    </DropdownMenuItem>
                  </>
                )}
                {(isEntreprise || isAdmin) && (
                  <DropdownMenuItem asChild>
                    <Link to="/request-feedback">Demander feedback candidat</Link>
                  </DropdownMenuItem>
                )}
                {(isCandidat || isAdmin) && (
                  <DropdownMenuItem asChild>
                    <Link to="/request-feedback">Demander feedback recruteur</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/profile">Mon profil</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings">Paramètres</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-red-500 focus:text-red-500">
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="outline" asChild className="hidden md:block border-blue-500 text-blue-600 hover:bg-blue-50">
                <Link to="/auth">Se connecter</Link>
              </Button>
              <Button className="bg-brandBlue hover:bg-blue-600 text-white" asChild>
                <Link to="/auth?tab=register">S'inscrire</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
